import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Divider, Icon, Tooltip, Position } from '@blueprintjs/core';
import { selectImages, selectCurrentSlideIndex, addImages, reorderImages, resetImages, removeImage, setCurrentSlide } from './ConfigurePresentationSlice';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './ConfigurationStyle.css';


const ConfigurePresentation = () => {
    const dispatch = useDispatch();
    const images = useSelector(selectImages);
    const currentSlideIndex = useSelector(selectCurrentSlideIndex);
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);

    // Upload images
    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);

        const newImages = files.map(file => ({
            name: file.name,
            size: file.size,
            url: URL.createObjectURL(file),
        }));

        dispatch(addImages(newImages));
    };

    // Upload folder
    const handleFolderUpload = (event) => {
        const files = Array.from(event.target.files).filter(file => file.type.startsWith('image/'));

        const newImages = files.map(file => ({
            name: file.name,
            size: file.size,
            url: URL.createObjectURL(file),
        }));

        dispatch(addImages(newImages));
    };

    const isDuplicateFile = (file, existingFiles) => {
        return existingFiles.some(existingFile =>
            existingFile.name === file.name && existingFile.size === file.size
        );
    };

    // Delete all slides
    const handleReset = () => {
        dispatch(resetImages());
    }

    // Delete single slide
    const handleDelete = (index) => {
        dispatch(removeImage(index));
    };

    // Drag-and-drop functionality
    const handleOnDragEnd = (result) => {
        if (!result.destination) return;
        const reorderedImages = Array.from(images);
        const [movedImage] = reorderedImages.splice(result.source.index, 1);
        reorderedImages.splice(result.destination.index, 0, movedImage);
        dispatch(reorderImages(reorderedImages));
    };

    // Navigation between slides
    const goToSlide = (index) => {
        if (index >= 0 && index < images.length) {
            dispatch(setCurrentSlide(index));
        }
    };

    return (
        <div className="config-options-container">
            <div className="custom-heading">
                Manage Presentation
            </div>
            <p className="custom-label" style={{ width: "auto" }}>
                Here you can upload images for later presentation in VR on the smart board.
            </p>
            <Divider/>

            <div className="config-section button-group">
                {/* IMAGES */}
                <p className="button-with-left-icon">
                    <input
                        type="file"
                        multiple
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                    />
                    <Button onClick={() => fileInputRef.current.click()}>
                        <Icon icon="document-share" style={{ marginRight: "5px" }} />
                        Upload Images
                    </Button>
                </p>

                {/* FOLDER */}
                <p className="button-with-left-icon">
                    <input
                        type="file"
                        webkitdirectory="true"
                        directory="true"
                        ref={folderInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFolderUpload}
                    />
                    <Button onClick={() => folderInputRef.current.click()}>
                        <Icon icon="folder-shared" style={{ marginRight: "5px" }} />
                        Upload Folder Content
                    </Button>
                </p>

                {/* RESET */}
                <p className="button-with-left-icon">
                    <Button onClick={handleReset}>
                        <Icon icon="eraser" style={{ marginRight: "5px" }} />
                        Reset Slides
                    </Button>
                </p>
            </div>

            <Divider />

            {/* DISPLAY SLIDES */}
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="slideList" direction="horizontal">
                    {(provided) => (
                        <div className="slide-list" {...provided.droppableProps} ref={provided.innerRef}>
                            {images.map((image, index) => (
                                <Draggable key={image.url} draggableId={image.url} index={index}>
                                    {(provided) => (
                                        <div
                                            className={`slide-tile ${index === currentSlideIndex ? 'active' : ''}`}
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            onClick={() => goToSlide(index)}
                                        >
                                            <Tooltip
                                                content={<img src={image.url} alt={`Slide ${index + 1}`} className="tooltip-image" />}
                                                position={Position.TOP}
                                            >
                                                <img
                                                    src={image.url}
                                                    alt={`Slide ${index + 1}`}
                                                />
                                            </Tooltip>
                                            <Icon
                                                icon="drag-handle-vertical"
                                                intent="none"
                                                className="drag-handle"
                                            />
                                            <div className="slide-number">{index + 1}</div>

                                            {/* Delete-Button */}
                                            <button
                                                className="delete-button"
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevents the image from being selected when the button is clicked
                                                    handleDelete(index);
                                                }}
                                            >
                                                X
                                            </button>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
};

export default ConfigurePresentation;